import React, { useState, useEffect, useCallback, useRef } from "react";
import styled from "styled-components";
import { Box, Flex } from "rebass/styled-components";
import { HiX, HiCheck, HiChevronLeft } from "react-icons/hi";
import { FiInfo, FiMaximize } from "react-icons/fi";
import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";

import { useRecoilValue } from "recoil";

import { ClipQualityIcon } from "../Icons";
import {
  useClipQualityConfigs,
  aspectRatioOfResolution,
  DEFAULT_ASPECT_RATIO,
  SliderStep,
} from "MVVM/Hooks/useClipQualityConfigs";
import { AllstarUser } from "MVVM/State";
import { analytics } from "MVVM/Analytics";
import { PreviewPlayer, PreviewPlayerHandle } from "./Components/PreviewPlayer";
import { FullscreenSettingsOverlay } from "./Components/FullscreenSettingsOverlay";
import { QualitySettingsSliders } from "./Components/QualitySettingsSliders";
import { AspectRatioSelect } from "./Components/AspectRatioSelect";

const { REACT_APP_S3_STATIC } = process.env;

const PREVIEW_PLAYER_WRAPPER_ID = "clip-quality-preview-wrapper";

interface ClipQualitySettingsModalProps {
  onClose: () => void;
  onUpgradeRequired?: (tierKey: string) => void;
  onAllSettings?: () => void;
}

const parseResolutionHeight = (resolution: string): number => {
  const match = resolution.match(/(\d+)x(\d+)/);
  return match ? parseInt(match[2], 10) : 0;
};

const nearestStepIndex = (
  steps: SliderStep[],
  target: number,
  valueOf: (step: SliderStep) => number,
): number => {
  let best = 0;
  steps.forEach((step, idx) => {
    if (
      Math.abs(valueOf(step) - target) < Math.abs(valueOf(steps[best]) - target)
    )
      best = idx;
  });
  return best;
};

export const ClipQualitySettingsModal = ({
  onClose,
  onUpgradeRequired,
  onAllSettings,
}: ClipQualitySettingsModalProps) => {
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_ASPECT_RATIO);

  const {
    configs,
    configsForAspect,
    aspectRatios,
    currentConfig,
    needsConfChange,
    resolutionSlider,
    framerateSlider,
    blurSlider,
    findConfigForSelection,
    isStepAvailable,
    updateConfig,
    saving,
    refetch,
    DEFAULT_TIER_CONFIGS,
  } = useClipQualityConfigs(aspectRatio);

  const allstarUser = useRecoilValue(AllstarUser);
  const isLoggedIn = allstarUser.loggedIn;
  const userTier = allstarUser.user?.userGroup?.type ?? "FREE";

  const savedConfigAvailable =
    currentConfig && !needsConfChange
      ? configs.find((c) => c.configName === currentConfig) ?? null
      : null;

  const defaultConfigObj =
    configs.find(
      (c) =>
        c.configName ===
        (DEFAULT_TIER_CONFIGS[userTier] ?? DEFAULT_TIER_CONFIGS.FREE),
    ) ?? null;

  const initConfigObj = savedConfigAvailable ?? defaultConfigObj;

  const [resIdx, setResIdx] = useState(0);
  const [fpsIdx, setFpsIdx] = useState(0);
  const [blurIdx, setBlurIdx] = useState(0);
  const [committedResIdx, setCommittedResIdx] = useState(0);
  const [committedFpsIdx, setCommittedFpsIdx] = useState(0);
  const [committedBlurIdx, setCommittedBlurIdx] = useState(0);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saved" | "failed">(
    "idle",
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [settingsOverlayOpen, setSettingsOverlayOpen] = useState(true);
  const initializedRef = React.useRef(false);
  const previewPlayerRef = useRef<PreviewPlayerHandle>(null);
  const openedAtRef = useRef(Date.now());
  const triedConfigsRef = useRef(new Set<string>());
  const fullscreenEnteredAtRef = useRef<number | null>(null);
  const fullscreenTotalMsRef = useRef(0);
  const closeFiredRef = useRef(false);
  const userTierRef = useRef(userTier);
  userTierRef.current = userTier;

  useEffect(() => {
    analytics.track("Clip Quality Settings - Modal Opened");
  }, []);

  useEffect(() => {
    if (isFullscreen) {
      setSettingsOverlayOpen(true);
      fullscreenEnteredAtRef.current = Date.now();
    } else if (fullscreenEnteredAtRef.current !== null) {
      fullscreenTotalMsRef.current +=
        Date.now() - fullscreenEnteredAtRef.current;
      fullscreenEnteredAtRef.current = null;
    }
  }, [isFullscreen]);

  useEffect(() => {
    if (initializedRef.current) return;
    if (!resolutionSlider.length) return;
    if (!initConfigObj) {
      initializedRef.current = true;
      return;
    }
    const savedAspect = aspectRatioOfResolution(
      initConfigObj.settings.resolution,
    );
    if (savedAspect !== aspectRatio && aspectRatios.includes(savedAspect)) {
      setAspectRatio(savedAspect);
      return;
    }
    const rIdx = resolutionSlider.findIndex(
      (s) => s.value === initConfigObj.settings.resolution,
    );
    const fIdx = framerateSlider.findIndex(
      (s) => s.value === initConfigObj.settings.framerate,
    );
    const bIdx = blurSlider.findIndex(
      (s) => s.value === initConfigObj.settings.frameBlendBlur,
    );
    if (rIdx >= 0) {
      setResIdx(rIdx);
      setCommittedResIdx(rIdx);
    }
    if (fIdx >= 0) {
      setFpsIdx(fIdx);
      setCommittedFpsIdx(fIdx);
    }
    if (bIdx >= 0) {
      setBlurIdx(bIdx);
      setCommittedBlurIdx(bIdx);
    }
    initializedRef.current = true;
  }, [
    initConfigObj,
    resolutionSlider,
    framerateSlider,
    blurSlider,
    aspectRatio,
    aspectRatios,
  ]);

  const selectedRes = resolutionSlider[resIdx]?.value as string;
  const selectedFps = framerateSlider[fpsIdx]?.value as number;
  const selectedBlur = blurSlider[blurIdx]?.value as number;

  const committedRes = resolutionSlider[committedResIdx]?.value as string;
  const committedFps = framerateSlider[committedFpsIdx]?.value as number;
  const committedBlur = blurSlider[committedBlurIdx]?.value as number;

  const selectedConf = findConfigForSelection(
    configsForAspect,
    selectedRes,
    selectedFps,
    selectedBlur,
  );

  const committedConf = findConfigForSelection(
    configsForAspect,
    committedRes,
    committedFps,
    committedBlur,
  );

  const isAlreadySaved =
    !!selectedConf &&
    !!currentConfig &&
    selectedConf.configName === currentConfig;

  const resAvailable = resolutionSlider[resIdx]
    ? isStepAvailable(
        configsForAspect,
        "resolution",
        resolutionSlider[resIdx].value,
        selectedRes,
        selectedFps,
        selectedBlur,
      )
    : true;
  const fpsAvailable = framerateSlider[fpsIdx]
    ? isStepAvailable(
        configsForAspect,
        "framerate",
        framerateSlider[fpsIdx].value,
        selectedRes,
        selectedFps,
        selectedBlur,
      )
    : true;
  const blurAvailable = blurSlider[blurIdx]
    ? isStepAvailable(
        configsForAspect,
        "frameBlendBlur",
        blurSlider[blurIdx].value,
        selectedRes,
        selectedFps,
        selectedBlur,
      )
    : true;
  const hasAnyUnavailable = !resAvailable || !fpsAvailable || !blurAvailable;

  const videoSrc =
    initializedRef.current && committedConf
      ? `${REACT_APP_S3_STATIC}/static/web/clipQualities/${committedConf.configName}.mp4`
      : null;

  const handleSliderRelease = useCallback(() => {
    setCommittedResIdx(resIdx);
    setCommittedFpsIdx(fpsIdx);
    setCommittedBlurIdx(blurIdx);
  }, [resIdx, fpsIdx, blurIdx]);

  const pendingAspectSnapRef = useRef<{
    resolution: string;
    framerate: number;
    frameBlendBlur: number;
  } | null>(null);

  const handleAspectRatioChange = (nextAspect: string) => {
    if (nextAspect === aspectRatio) return;
    pendingAspectSnapRef.current = {
      resolution: selectedRes,
      framerate: selectedFps,
      frameBlendBlur: selectedBlur,
    };
    setAspectRatio(nextAspect);
    analytics.track("Clip Quality Settings - Aspect Ratio Changed", {
      aspectRatio: nextAspect,
      userGroup: userTier,
    });
  };

  // after an aspect ratio switch rebuilds the sliders, snap each slider to
  // the step closest to the previous selection
  useEffect(() => {
    const pending = pendingAspectSnapRef.current;
    if (!pending || !resolutionSlider.length) return;
    pendingAspectSnapRef.current = null;

    const rIdx = nearestStepIndex(
      resolutionSlider,
      parseResolutionHeight(pending.resolution),
      (step) => parseResolutionHeight(step.value as string),
    );
    const fIdx = nearestStepIndex(
      framerateSlider,
      pending.framerate,
      (step) => step.value as number,
    );
    const bIdx = nearestStepIndex(
      blurSlider,
      pending.frameBlendBlur,
      (step) => step.value as number,
    );
    setResIdx(rIdx);
    setCommittedResIdx(rIdx);
    setFpsIdx(fIdx);
    setCommittedFpsIdx(fIdx);
    setBlurIdx(bIdx);
    setCommittedBlurIdx(bIdx);
  }, [resolutionSlider, framerateSlider, blurSlider]);

  useEffect(() => {
    if (selectedConf) {
      triedConfigsRef.current.add(selectedConf.configName);
    }
  }, [selectedConf]);

  const handleSave = async () => {
    if (!selectedConf) return;

    if (!selectedConf.canApply) {
      analytics.track("Clip Quality Settings - Upgrade Popup Shown", {
        userGroup: userTier,
        configName: selectedConf.configName,
      });
      onUpgradeRequired?.(selectedConf.requiredUserGroup);
      return;
    }

    try {
      const result = await updateConfig({
        variables: { configName: selectedConf.configName },
      });
      if (result.data?.updateClipQualityConfig?.success) {
        setSaveStatus("saved");
        analytics.track("Clip Quality Settings - Settings Saved", {
          configName: selectedConf.configName,
          userGroup: userTier,
        });
        refetch();
      } else {
        setSaveStatus("failed");
      }
    } catch {
      setSaveStatus("failed");
    }

    setTimeout(() => setSaveStatus("idle"), 3000);
  };

  const handleFullscreenStepAvailable = useCallback(
    (settingKey: string, value: string | number) => {
      return isStepAvailable(
        configsForAspect,
        settingKey as keyof (typeof configsForAspect)[0]["settings"],
        value,
        selectedRes,
        selectedFps,
        selectedBlur,
      );
    },
    [configsForAspect, isStepAvailable, selectedRes, selectedFps, selectedBlur],
  );

  const fireCloseAnalytics = () => {
    if (closeFiredRef.current) return;
    closeFiredRef.current = true;
    const timeSpentMs = Date.now() - openedAtRef.current;
    let fullscreenMs = fullscreenTotalMsRef.current;
    if (fullscreenEnteredAtRef.current !== null) {
      fullscreenMs += Date.now() - fullscreenEnteredAtRef.current;
    }
    analytics.track("Clip Quality Settings - Modal Closed", {
      userGroup: userTierRef.current,
      configsTried: triedConfigsRef.current.size,
      timeSpentSeconds: Math.round(timeSpentMs / 1000),
      fullscreenTimeSpentSeconds: Math.round(fullscreenMs / 1000),
    });
  };

  useEffect(() => {
    return () => fireCloseAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClose = useCallback(() => {
    fireCloseAnalytics();
    onClose();
  }, [onClose]);

  if (!resolutionSlider.length) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleClose}>
          <HiX size={20} />
        </CloseButton>

        <Header>
          <ClipQualityIcon size={28} />
          <Title>Clip Quality Settings</Title>
        </Header>

        {isLoggedIn && !currentConfig && (
          <HelpText>
            You can always come back to adjust these settings by clicking on
            your profile icon in the top-right corner
          </HelpText>
        )}

        {aspectRatios.length > 1 && (
          <AspectRatioRow>
            <AspectRatioLabel>Aspect Ratio</AspectRatioLabel>
            <AspectRatioSelect
              value={aspectRatio}
              options={aspectRatios}
              onChange={handleAspectRatioChange}
            />
          </AspectRatioRow>
        )}

        {aspectRatio !== DEFAULT_ASPECT_RATIO && (
          <AspectRatioWarning>
            Using aspect ratios other than 16:9 will cause some Personalizations
            not to work - for example Intros, Outros and other full-screen
            overlay type customizations
          </AspectRatioWarning>
        )}

        <ModalSlidersWrapper>
          <QualitySettingsSliders
            resolutionSlider={resolutionSlider}
            framerateSlider={framerateSlider}
            blurSlider={blurSlider}
            resIdx={resIdx}
            fpsIdx={fpsIdx}
            blurIdx={blurIdx}
            onResChange={setResIdx}
            onFpsChange={setFpsIdx}
            onBlurChange={setBlurIdx}
            onSliderRelease={handleSliderRelease}
            isStepAvailableFn={handleFullscreenStepAvailable}
            hasAnyUnavailable={hasAnyUnavailable}
            variant="modal"
          />
        </ModalSlidersWrapper>

        <SliderPreviewDivider />

        <PreviewSection>
          <PreviewHeader>
            <PreviewTitle>Quality Settings Preview</PreviewTitle>
            <FullscreenButtonRow>
              <AnimatedTooltip
                content="Edit the settings in fullscreen mode to see the differences better."
                position="top"
              >
                <FullscreenTooltipTrigger>
                  <FiInfo size={14} />
                </FullscreenTooltipTrigger>
              </AnimatedTooltip>
              <PlayFullscreenButton
                onClick={() => {
                  previewPlayerRef.current?.enterFullscreenOnly();
                }}
              >
                Edit in fullscreen <FiMaximize size={14} />
              </PlayFullscreenButton>
            </FullscreenButtonRow>
          </PreviewHeader>
          <PreviewPlayerWrapper id={PREVIEW_PLAYER_WRAPPER_ID}>
            <PreviewPlayer
              ref={previewPlayerRef}
              mp4Source={videoSrc}
              wrapperId={PREVIEW_PLAYER_WRAPPER_ID}
              onFullscreenChange={setIsFullscreen}
            />
            {!videoSrc && (
              <NoPreview>No preview available for this combination</NoPreview>
            )}
            {isFullscreen && (
              <FullscreenSettingsOverlay
                aspectRatio={aspectRatio}
                aspectRatios={aspectRatios}
                onAspectRatioChange={handleAspectRatioChange}
                resolutionSlider={resolutionSlider}
                framerateSlider={framerateSlider}
                blurSlider={blurSlider}
                resIdx={resIdx}
                fpsIdx={fpsIdx}
                blurIdx={blurIdx}
                onResChange={setResIdx}
                onFpsChange={setFpsIdx}
                onBlurChange={setBlurIdx}
                onSliderRelease={handleSliderRelease}
                isStepAvailableFn={handleFullscreenStepAvailable}
                hasAnyUnavailable={hasAnyUnavailable}
                isOpen={settingsOverlayOpen}
                onToggle={() => setSettingsOverlayOpen((prev) => !prev)}
              />
            )}
          </PreviewPlayerWrapper>
        </PreviewSection>

        {isLoggedIn && (
          <SaveArea>
            <Footer>
              {onAllSettings && (
                <AllSettingsLink onClick={onAllSettings}>
                  <HiChevronLeft size={14} /> All Clipping Settings
                </AllSettingsLink>
              )}
              {saveStatus === "saved" && (
                <SaveFeedback $success>
                  <HiCheck size={16} /> Saved
                </SaveFeedback>
              )}
              {saveStatus === "failed" && (
                <SaveFeedback $success={false}>
                  <HiX size={16} /> Failed saving
                </SaveFeedback>
              )}
              <SaveButton
                onClick={handleSave}
                disabled={!selectedConf || saving || isAlreadySaved}
              >
                {saving
                  ? "Saving..."
                  : isAlreadySaved
                  ? "Already saved"
                  : "Save Settings"}
              </SaveButton>
            </Footer>
          </SaveArea>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  bottom: 0;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
`;

const ModalContainer = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.ice};
  flex-direction: column;
  max-height: 90vh;
  max-width: 520px;
  overflow-y: auto;
  padding: 28px;
  position: relative;
  width: 90vw;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    border-radius: 4px;
  }
`;

const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;
  position: absolute;
  right: 16px;
  top: 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const Header = styled(Flex)`
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
`;

const Title = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 23px;
  font-weight: 600;
  margin: 0;
`;

const HelpText = styled(Box)`
  background: ${({ theme }) => theme.colors.envyAlpha10};
  border-left: 3px solid ${({ theme }) => theme.colors.envy};
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 15px;
  margin: 0 0 16px 0;
  padding: 10px 14px;
`;

const AspectRatioRow = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const AspectRatioLabel = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 16px;
  font-weight: 600;
`;

const AspectRatioWarning = styled(Box)`
  background: rgba(245, 166, 35, 0.08);
  border-left: 3px solid #f5a623;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  margin: 0 0 16px 0;
  padding: 10px 14px;
`;

const ModalSlidersWrapper = styled(Box)`
  margin-bottom: 12px;
`;

const SliderPreviewDivider = styled(Box)`
  background: ${({ theme }) => theme.colors.chalkAlpha20};
  height: 1px;
  margin-bottom: 20px;
  width: 100%;
`;

const PreviewSection = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const PreviewHeader = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
`;

const FullscreenButtonRow = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const FullscreenTooltipTrigger = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: help;
  display: inline-flex;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const PreviewTitle = styled(Box)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: "Barlow", sans-serif;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
`;

const PlayFullscreenButton = styled.button`
  align-items: center;
  background: linear-gradient(227.35deg, #72439a 6.85%, #0093a8 89.04%);
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: inline-flex;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  font-weight: 600;
  gap: 6px;
  padding: 8px 16px;
  transition: opacity 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PreviewPlayerWrapper = styled(Box)`
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  width: 100%;

  &:fullscreen {
    align-items: center;
    border-radius: 0;
    display: flex;

    /* fill the screen and let the video element letterbox naturally,
       same as the normal clip player (EmbeddablePlayerWrapper) */
    .video-js {
      height: 100vh !important;
      padding-top: 0 !important;
      width: 100vw !important;
    }

    .vjs-tech {
      height: 100% !important;
      width: 100% !important;
    }
  }
`;

const NoPreview = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  bottom: 0;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 16px;
  justify-content: center;
  left: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  z-index: 5;
`;

const Footer = styled(Flex)`
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
`;

const AllSettingsLink = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: inline-flex;
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
  gap: 2px;
  margin-right: auto;
  padding: 0;
  transition: color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const SaveFeedback = styled(Flex)<{ $success: boolean }>`
  align-items: center;
  color: ${({ $success }) => ($success ? "#4dbd94" : "#e74c3c")};
  display: inline-flex;
  font-size: 15px;
  font-weight: 600;
  gap: 4px;
`;

const SaveButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-family: "Barlow", sans-serif;
  font-size: 16px;
  font-weight: 600;
  padding: 10px 20px;
  transition:
    opacity 0.2s,
    transform 0.1s;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const SaveArea = styled(Flex)`
  flex-direction: column;
  position: relative;
`;
